import { motion } from "framer-motion";
import { fadeUp, fade, stagger } from "../animations";

export default function About() {
  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fade}
      className="max-w-6xl mx-auto px-6 py-16 space-y-20"
    >
      {/* ================= HEADER ================= */}
      <motion.div variants={fadeUp}>
        <h2 className="text-2xl font-semibold mb-4 text-yellow-300">
          About Me
        </h2>
        <p className="text-gray-400 max-w-3xl">
          A passionate Full Stack Developer and AI/ML Engineer who enjoys building
          intelligent, scalable, and user-focused software solutions.
        </p>
      </motion.div>

      {/* ================= MAIN CONTENT ================= */}
      <motion.div
        variants={stagger}
        className="grid md:grid-cols-2 gap-12"
      >
        {/* LEFT – STORY */}
        <motion.div variants={fadeUp} className="text-gray-400 leading-relaxed space-y-4">
          <p>
            I am a <span className="text-white font-medium">Full Stack Developer and AI/ML Engineer</span>
            with hands-on experience in Python, Flask, PHP, CodeIgniter, Flutter,
            and MySQL. I specialize in building end-to-end applications that
            combine traditional full stack development with AI-driven intelligence.
          </p>

          <p>
            My work includes developing REST APIs, admin dashboards, ERP-style
            systems, mobile applications, and integrating machine learning
            features such as <span className="text-white">OCR, NLP, classification, and automation</span>.
          </p>

          <p>
            Over the years, I have successfully built and deployed
            <span className="text-white"> 15+ real-time projects </span>
            on production servers and mentored
            <span className="text-white"> 50+ students </span>
            in Machine Learning and Full Stack development for both academic and
            industry-level use cases.
          </p>
        </motion.div>

        {/* RIGHT – QUICK INFO */}
        <motion.div
          variants={fadeUp}
          className="bg-[#0b1020] p-6 rounded-xl border border-white/10 text-gray-400 space-y-3"
        >
          <h3 className="font-semibold text-white mb-2">Quick Info</h3>

          <p>📍 <span className="text-white">Shimoga, Karnataka, India</span></p>
          <p>🎓 <span className="text-white">MCA</span> – Kuvempu University</p>
          <p>
            💼 <span className="text-white">Full Stack Developer</span> –  
            Sri Sadguru Hyper Technologies Pvt Ltd
          </p>
          <p>👩‍🏫 Mentored <span className="text-white">50+ students</span></p>
          <p>💻 Working with <span className="text-white">Web, Mobile & AI systems</span></p>
        </motion.div>
      </motion.div>

      {/* ================= CORE VALUES ================= */}
      <motion.div variants={fadeUp}>
        <h3 className="text-xl font-semibold mb-6 text-yellow-300">
          What Drives Me
        </h3>

        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: "Problem Solving",
              desc: "I enjoy breaking down complex problems into simple, scalable solutions."
            },
            {
              title: "Continuous Learning",
              desc: "Actively learning new technologies in AI, backend systems, and mobile development."
            },
            {
              title: "Mentorship & Impact",
              desc: "Helping students and teams grow through clear guidance and real-world exposure."
            }
          ].map((item) => (
            <motion.div
              key={item.title}
              whileHover={{ y: -6 }}
              className="bg-[#0b1020] p-6 rounded-xl border border-white/10"
            >
              <h4 className="font-semibold mb-2">{item.title}</h4>
              <p className="text-gray-400 text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ================= CTA ================= */}
      <motion.div
        variants={fadeUp}
        className="text-center bg-gradient-to-r from-[#1b254b] to-[#050814] p-10 rounded-2xl border border-yellow-400/30"
      >
        <h3 className="text-xl font-semibold mb-3">
          Looking for a developer who delivers real-world solutions?
        </h3>
        <p className="text-gray-400 mb-6">
          I’m open to full-time roles, freelance projects, and collaboration opportunities.
        </p>

        <a
          href="/contact"
          className="inline-block px-8 py-3 rounded-full bg-gradient-to-r from-blue-400 to-yellow-300 text-black font-semibold"
        >
          Get in Touch
        </a>
      </motion.div>
    </motion.section>
  );
}
